import { ActivityEnrichment, QueuedEvent, WebActivityIdentity } from './types';

/**
 * IMBEE-8954 — the beacon transport (TDS §6f-ii).
 *
 * ⚠ `navigator.sendBeacon` IS UNUSABLE HERE AND MUST NOT BE INTRODUCED. It cannot set custom
 * headers, and this endpoint authenticates with `x-imbee-web-chat-token` — exactly the fault that
 * broke `tracking-plugin` (`DC-T114-99` break #2). `fetch(..., { keepalive: true })` carries headers
 * AND survives page unload, so it is strictly better here; its 64KB body cap is what bounds
 * `BATCH_SIZE` in the controller.
 *
 * The header is kept rather than moved to a query string because `x-imbee-web-chat-token` is the
 * convention across the whole visitor tier (~20 endpoints); a query token would also be written into
 * access logs, history and `Referer` for no capability gain.
 */

/** Same gateway origin as the campaign calls — both are visitor-tier routes on web-chat-service. */
const BASE_URL = process.env.REACT_APP_LIVE_CHAT_CAMPAIGN_GATEWAY_URL || '';
export const EVENTS_PATH = '/events';

export type BeaconBody = ActivityEnrichment & {
  visitorToken: string;
  currentPage?: string;
  events: Array<Omit<QueuedEvent, 'id'>>;
};

class WebActivityClient {
  private identity: WebActivityIdentity;

  constructor(identity: WebActivityIdentity) {
    this.identity = identity;
  }

  /**
   * Resolves TRUE only on a confirmed 2xx — the caller removes queue items on that signal alone.
   *
   * A 204 counts as success even though it proves nothing was PERSISTED: an unknown widget token,
   * an opted-out visitor and a failed Mongo write all return 204 (the endpoint is public and must
   * never error a customer's site). Retrying on 204 would therefore loop forever, so the client's
   * success condition is DELIVERY, not persistence.
   */
  async send(body: BeaconBody): Promise<boolean> {
    const widgetToken = this.identity.getWidgetToken();
    if (!widgetToken) return false;

    try {
      const response = await fetch(`${BASE_URL}${EVENTS_PATH}`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'x-imbee-web-chat-token': widgetToken,
        },
        body: JSON.stringify(body),
      });
      return response.ok;
    } catch {
      // Network failure, or the browser refused a keepalive request that exceeded its in-flight
      // quota. Either way the items stay queued and the next trigger retries.
      return false;
    }
  }
}

export default WebActivityClient;
